import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  FaInfoCircle,
  FaEye,
  FaEyeSlash,
  FaStore,
  FaPhoneAlt,
  FaLock,
} from "react-icons/fa";

import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useLoginMutation } from "../slices/userApiSlice";
import { useGetInformationsQuery } from "../slices/informationApiSlice";
import { setCredentials } from "../slices/authSlice";
import { toast } from "react-toastify";

import "../css/LoginScreen.css";

const LoginScreen = () => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [secureText, setSecureText] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const redirect = new URLSearchParams(location.search).get("redirect") || "/Home";

  const [login, { isLoading }] = useLoginMutation();
  const { data: infoData } = useGetInformationsQuery();
  const { userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [userInfo, redirect, navigate]);

  const validatePhone = (phone) => /^[0-9]{10}$/.test(phone);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validatePhone(phone)) {
      return toast.error("Please enter a valid 10-digit Indian phone number");
    }

    try {
      const res = await login({ phone, password }).unwrap();
      dispatch(setCredentials(res));
      toast.success("Login successful!");
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <div className="login-bg">

      <Container className="py-4">
        <Row className="justify-content-center">

          <Col lg={8}>

            {/* ⭐ Announcements */}
            {infoData?.map((item) => (
              <Alert
                variant="info"
                className="d-flex align-items-center rounded-4 shadow-sm custom-announcement"
                key={item._id}
              >
                <FaInfoCircle size={24} className="me-2 text-primary" />
                <span className="fw-semibold">{item.information}</span>
              </Alert>
            ))}

            {/* ⭐ Title */}
            <h2 className="text-center mt-3 fw-bold welcome-title">
              Welcome Back 👋
            </h2>
          </Col>

          {/* ⭐ Glass Login Card */}
          <Col xs={12} md={8} lg={5}>
            <Card className="glass-card p-4 mt-3 shadow-lg">

              <Form onSubmit={submitHandler}>

                {/* Phone */}
                <Form.Label className="fw-bold text-primary">
                  Phone Number
                </Form.Label>
                <div className="input-with-icon mb-3">
                  <FaPhoneAlt className="input-icon" />
                  <Form.Control
                    type="text"
                    maxLength={10}
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>

                {/* Password */}
                <Form.Label className="fw-bold text-primary">
                  Password
                </Form.Label>
                <div className="input-with-icon mb-3">
                  <FaLock className="input-icon" />
                  <Form.Control
                    type={secureText ? "password" : "text"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    className="toggle-eye"
                    onClick={() => setSecureText(!secureText)}
                  >
                    {secureText ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-100 gradient-btn py-2 fw-bold"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Form>

              {/* Links */}
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  className="p-0 link-custom"
                  onClick={() => navigate("/register")}
                >
                  New Customer? Register
                </Button>
                <br />
                <Button
                  variant="link"
                  className="p-0 link-custom"
                  onClick={() => navigate(`/forgot-password?redirect=${redirect}`)}
                >
                  Forgot Password?
                </Button>
              </div>
            </Card>
          </Col>

          {/* ⭐ Description */}
          <Col xs={12} className="mt-4 px-4 d-flex">
            <FaStore size={32} className="text-primary me-3" />
            <p className="description-text">
              Welcome to
              <span className="fw-bold text-primary"> Karaikudi Local Cart</span> —
              your trusted marketplace for groceries, home essentials, snacks, and
              Karaikudi specials.  
              Fast delivery. Fresh products. Local prices. 🚚✨
            </p>
          </Col>

        </Row>
      </Container>

    </div>
  );
};

export default LoginScreen;
